import axios from "axios";
import { type ClassValue, clsx } from "clsx";
import { useState, useEffect } from "react";
import { twMerge } from "tailwind-merge";
import { VITE_ZEM_GATEWAY_URL } from "./constants";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export function getCurrentTime() {
	return new Date().toLocaleString("en-AU", {
		timeZone: "Australia/Brisbane",
	});
}

export function useSearchEngineDetector() {
	const searchEngines = [
		{ name: "Google", domain: "google." },
		{ name: "Bing", domain: "bing.com" },
		{ name: "Yahoo", domain: "yahoo." },
		{ name: "DuckDuckGo", domain: "duckduckgo.com" },
	];

	const [searchEngine, setSearchEngine] = useState("Direct");

	useEffect(() => {
		const referrerUrl = document.referrer;

		if (!referrerUrl) {
			setSearchEngine("Direct");
			return;
		}

		try {
			const referrerHost = new URL(referrerUrl).hostname;

			const detectedEngine = searchEngines.find((engine) =>
				referrerHost.includes(engine.domain),
			);

			setSearchEngine(detectedEngine ? detectedEngine.name : `Other: ${referrerHost}`);
		} catch (error) {
			console.error("Error parsing referrer URL:", error);
			setSearchEngine("Unknown");
		}
	}, []);

	return searchEngine;
}

export async function fetchPartnerInfo(id: string) {
	try {
		const siteUrl = window.location.origin;
		const response = await axios.get(`${siteUrl}/wp-json/wp/v2/pages/${id}`, {
			headers: {
				"X-THEME": "true",
			},
		});

		return response.data;
	} catch (error) {
		console.error("Error fetching partner information:", error);
		throw new Error("Failed to fetch partner information");
	}
}

const ZAP_LOCK_KEY = "isSendingZap";
const ZAP_LOCK_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

export async function postZap<T>(data: T) {
	// Prevent duplicate sends across tabs
	const lockValue = localStorage.getItem(ZAP_LOCK_KEY);

	if (lockValue) {
		const lockTime = Number(lockValue);
		const now = Date.now();

		// If lock is still valid, block duplicate
		if (now - lockTime < ZAP_LOCK_TIMEOUT_MS) {
			console.warn("Zap already being sent. Duplicate blocked.");
			return { success: false, message: "Zap is already being sent." };
		}
	}

	// Set lock
	localStorage.setItem(ZAP_LOCK_KEY, String(Date.now()));

	try {
		const response = await fetch(`${VITE_ZEM_GATEWAY_URL}/api/send-data`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ data }),
		});
		if (!response.ok) {
			const errorText = await response.text();
			console.error(`Error in sending data to the server. Status: `, response.status);
			console.error("Response body:", errorText);
			return;
		}
		const responseData = await response.json();
		return responseData;
	} catch (error) {
		console.error(`Network error while sending data to the server: `, error);
	} finally {
		// Remove lock after request finishes
		localStorage.removeItem(ZAP_LOCK_KEY);
	}
}

const EMAIL_LOCK_KEY = "isSendingEmail";
const LOCK_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

export async function sendEmail<T>(data: T) {
	// Prevent duplicate sends across tabs
	const lockValue = localStorage.getItem(EMAIL_LOCK_KEY);

	if (lockValue) {
		const lockTime = Number(lockValue);
		const now = Date.now();

		// If lock is still valid, block duplicate
		if (now - lockTime < LOCK_TIMEOUT_MS) {
			console.warn("Email already being sent. Duplicate blocked.");
			return { success: false, message: "Email is already being sent." };
		}
	}

	// Set lock
	localStorage.setItem(EMAIL_LOCK_KEY, String(Date.now()));

	try {
		const response = await fetch(`${VITE_ZEM_GATEWAY_URL}/api/send-email`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ data }),
		});
		if (!response.ok) {
			const errorText = await response.text();
			console.error(`Error in sending email data to the server. Status: `, response.status);
			console.error("Response body:", errorText);
			return;
		}
		const responseData = await response.json();
		return responseData;
	} catch (error) {
		console.error(`Network error while sending email: `, error);
	} finally {
		// Remove lock after request finishes
		localStorage.removeItem(EMAIL_LOCK_KEY);
	}
}
